import { useCallback, useEffect, useMemo, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { getIntegrationTypeName, HASH_TO_INTEGRATION, Integration } from "@common/models/integrations";
import useWorkspaceAppsApiConsumer from "@main/consumers/useWorkspaceAppsApiConsumer";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useParams, useSearchParams } from "react-router-dom";

import { useAdministrationDeps } from "../Administration";
import { determineInitialSelectedAppId } from "../util/determineInitialSelectedApp";

export default function useAppsFeedbackViewConsumer() {
  const [params, setParams] = useSearchParams();
  const { administrationService } = useAdministrationDeps();
  const { fetchAllApps } = useWorkspaceAppsApiConsumer();
  const { appKey } = useParams();

  const { apps } = useAppContext();
  const { selectedWorkspace, selectedApp: globalSelectedApp } = useAppSelectionContext();

  const preSelectedIntegrationType = useMemo(() => {
    // Read pre-selected integration type from type param or hash. It is used to open proper modal.
    const typeParam = params.get("type");
    if (typeParam && HASH_TO_INTEGRATION[typeParam]) {
      return HASH_TO_INTEGRATION[typeParam];
    }

    const hash = window.location.hash.slice(1);
    return HASH_TO_INTEGRATION[hash] ?? undefined;
  }, [params]);

  useEffect(() => {
    // Remove type param if it exists. It is used to open proper modal on redirect.
    const typeParam = params.get("type");
    if (!typeParam) return;

    const newParams = new URLSearchParams(params);
    newParams.delete("type");
    setParams(newParams, { replace: true });
  }, [params, setParams]);

  const [localSelectedAppId, setLocalSelectedAppId] = useState<string | undefined>(
    determineInitialSelectedAppId(globalSelectedApp, apps, window.location.search, appKey),
  );
  const localSelectedApp = useMemo(() => apps.find((app) => app.id === localSelectedAppId), [localSelectedAppId, apps]);

  const [loading, setLoading] = useState(false);

  const disconnectIntegration = useCallback(
    async (integration: Integration) => {
      if (!selectedWorkspace?.id || !localSelectedApp) return;

      try {
        setLoading(true);

        await administrationService.disconnectIntegrationByType(
          selectedWorkspace.id,
          localSelectedApp.id,
          integration.type,
        );

        await fetchAllApps(selectedWorkspace.id);

        displayToast({
          content: `${getIntegrationTypeName(integration.type)} has been disconnected`,
        });
      } catch (error) {
        displayToast({
          title: "Something went wrong",
          content: error?.response?.data?.message ?? "Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [localSelectedApp, selectedWorkspace, administrationService, fetchAllApps],
  );

  const exportTickets = useCallback(async () => {
    setLoading(true);
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;

      await administrationService.exportTickets(selectedWorkspace.id, localSelectedApp.id);

      displayToast({
        title: "Export started",
        content: "Check your email to find a .csv file",
      });
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    }
    setLoading(false);
  }, [localSelectedApp, selectedWorkspace, administrationService]);

  useEffect(() => {
    if (appKey) {
      setLocalSelectedAppId(determineInitialSelectedAppId(globalSelectedApp, apps, window.location.search, appKey));
    }
  }, [appKey, apps, globalSelectedApp]);

  return {
    disconnectIntegration,
    allApps: apps,
    preSelectedIntegrationType,
    loading,
    localSelectedApp,
    exportTickets,
  };
}
